// Filter definitions and their URL/API encoding (pure; no React). Used by FilterBar/useListState.
import { money as fmtMoney, number as fmtNumber } from '../../lib/format'
import { DATE_PRESETS, rangeLabel, resolvePreset, type DatePreset, type DateRange } from './date-range'


interface Opt { value: string; label: string }
export type FilterDef =
  | { id: string; label: string; type: 'multi'; options: Opt[] }
  | { id: string; label: string; type: 'select'; options: Opt[] }
  | { id: string; label: string; type: 'boolean'; trueLabel?: string; falseLabel?: string }
  | { id: string; label: string; type: 'text'; placeholder?: string }
  /** `money: true` → values are integer MINOR units (shown with MoneyInput, sent as "1000..5000"). */
  | { id: string; label: string; type: 'number-range'; unit?: string; money?: boolean }
  /** API params: the resolved days go out as `from`/`to` (the orders/analytics convention); set
   *  `apiParams` when a list has a second date filter, e.g. { from: 'updatedFrom', to: 'updatedTo' }. */
  | { id: string; label: string; type: 'date-range'; apiParams?: { from: string; to: string } }

/** Values keyed by filter id: multi → string[], select/text → string, boolean → boolean,
 *  number-range → { min?, max? } (money in minor units), date-range → DateRange. */
export type FilterValues = Record<string, unknown>
export interface NumberRange { min?: number | null; max?: number | null }

export const isEmptyValue = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && v !== null && 'min' in (v as object) && (v as NumberRange).min == null && (v as NumberRange).max == null)

/**
 * Encode a filter value for the URL ("paid,pending", "100..500", "last_30_days", "2026-09-01..2026-09-26").
 * Date presets stay symbolic in the URL so a shared "Last 30 days" link stays relative; the API gets
 * resolved days from filterQueryParams().
 */
export function encodeFilter(def: FilterDef, v: unknown): string | undefined {
  if (isEmptyValue(v)) return undefined
  switch (def.type) {
    case 'multi': return Array.isArray(v) ? v.map(String).join(',') : undefined
    case 'select': case 'text': return String(v)
    case 'boolean': return v === true ? 'true' : v === false ? 'false' : undefined
    case 'number-range': { const r = v as NumberRange; return `${r.min ?? ''}..${r.max ?? ''}` }
    case 'date-range': { const r = v as DateRange; return r.preset && r.preset !== 'custom' ? r.preset : `${r.from}..${r.to}` }
  }
}

/**
 * API query params for one filter value (what `useListState().query` sends): date ranges become
 * resolved store-local days `{ from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }` (or `apiParams` names);
 * everything else is `{ [def.id]: encodeFilter(def, v) }`. Empty values give `{}`.
 */
export function filterQueryParams(def: FilterDef, v: unknown): Record<string, string> {
  if (isEmptyValue(v)) return {}
  if (def.type === 'date-range') {
    const r = v as DateRange
    const days = r.preset && r.preset !== 'custom' ? resolvePreset(r.preset) : r
    if (!isDayKey(days.from) || !isDayKey(days.to) || days.from > days.to) return {}
    const names = def.apiParams ?? { from: 'from', to: 'to' }
    return { [names.from]: days.from, [names.to]: days.to }
  }
  const enc = encodeFilter(def, v)
  return enc === undefined ? {} : { [def.id]: enc }
}

/** A real calendar day "YYYY-MM-DD" (rejects 2026-02-31, 2026-13-01). */
export function isDayKey(s: unknown): s is string {
  if (typeof s !== 'string') return false
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return false
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3])
  if (y < 1970 || y > 2999 || mo < 1 || mo > 12 || d < 1) return false
  return d <= new Date(Date.UTC(y, mo, 0)).getUTCDate()
}

const MAX_RANGE = Number.MAX_SAFE_INTEGER

/** Decode a URL value; anything tampered, malformed or impossible decodes to undefined (filter off). */
export function decodeFilter(def: FilterDef, s: string | null): unknown {
  if (s === null || s === '') return undefined
  switch (def.type) {
    case 'multi': { const allowed = new Set(def.options.map(o => o.value)); const vals = [...new Set(s.split(','))].filter(x => allowed.has(x)); return vals.length ? vals : undefined }
    case 'select': return def.options.some(o => o.value === s) ? s : undefined
    case 'text': { const t = s.trim().slice(0, 200); return t || undefined }
    case 'boolean': return s === 'true' ? true : s === 'false' ? false : undefined
    case 'number-range': {
      // Money ranges are integer minor units; plain number ranges may carry decimals.
      const num = def.money ? '-?\\d{1,16}' : '-?\\d{1,16}(?:\\.\\d{1,6})?'
      const m = new RegExp(`^(${num})?\\.\\.(${num})?$`).exec(s)
      if (!m || (!m[1] && !m[2])) return undefined
      const min = m[1] ? Number(m[1]) : null, max = m[2] ? Number(m[2]) : null
      if ((min !== null && Math.abs(min) > MAX_RANGE) || (max !== null && Math.abs(max) > MAX_RANGE)) return undefined
      if (min !== null && max !== null && min > max) return undefined
      return { min, max }
    }
    case 'date-range': {
      if (DATE_PRESETS.some(p => p.id === s)) return resolvePreset(s as Exclude<DatePreset, 'custom'>)
      const m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(s)
      return m && isDayKey(m[1]) && isDayKey(m[2]) && m[1] <= m[2] ? { from: m[1], to: m[2], preset: 'custom' } : undefined
    }
  }
}

export function filterChipLabel(def: FilterDef, v: unknown): string {
  switch (def.type) {
    case 'multi': {
      const labels = (v as string[]).map(x => def.options.find(o => o.value === x)?.label ?? x)
      return labels.length > 2 ? `${labels.slice(0, 2).join(', ')} +${labels.length - 2}` : labels.join(', ')
    }
    case 'select': return def.options.find(o => o.value === v)?.label ?? String(v)
    case 'boolean': return v ? (def.trueLabel ?? 'Yes') : (def.falseLabel ?? 'No')
    case 'text': return `“${String(v)}”`
    case 'number-range': {
      const r = v as NumberRange
      const f = (n: number) => (def.money ? fmtMoney(n) : `${fmtNumber(n)}${def.unit ? ` ${def.unit}` : ''}`)
      if (r.min != null && r.max != null) return `${f(r.min)} – ${f(r.max)}`
      return r.min != null ? `≥ ${f(r.min)}` : `≤ ${f(r.max!)}`
    }
    case 'date-range': return rangeLabel(v as DateRange)
  }
}

